import React, { useCallback, useMemo } from "react";
import { Breadcrumbs } from "web/react/components/breadcrumbs/breadcrumbs";
import { Accordion } from "web/react/emo/accordion";
import { Button } from "web/react/emo/button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { sendAnalyticsEvent } from "web/react/pages/product/out-of-stock/out-of-stock-product-page/utils";
import { ProductImageGallery } from "web/react/pages/product/product-image-gallery/product-image-gallery";
import { gettext } from "web/script/modules/django-i18n";
import { BreadcrumbsSerializer, ProductPageAreaSerializer } from "web/types/serializers";
import * as styles from "./product-details.css";

interface ProductDetailsProps {
    topOfPDPRef: React.RefObject<HTMLDivElement>;
    isOverlay?: boolean;
    productArea: ProductPageAreaSerializer;
    productBreadcrumbs: BreadcrumbsSerializer | null;
}

export function ProductDetails({
    topOfPDPRef,
    isOverlay,
    productArea,
    productBreadcrumbs,
}: ProductDetailsProps): React.ReactElement {
    const productDetailsAccordion = useMemo(() => {
        return {
            title: gettext("in_stock_product.details"),
            children: (
                <Text textStyle="body-s" color="secondary" dataTestId="product-description">
                    {productArea.product.long_description}
                </Text>
            ),
            isOpen: true,
        };
    }, [productArea.product.long_description]);

    const scrollToTop = useCallback(() => {
        sendAnalyticsEvent("back-to-top", "clicked", "", true, {
            product_id: productArea.product.product_id,
        });
        if (isOverlay) {
            topOfPDPRef.current?.scrollIntoView({ behavior: "smooth" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [topOfPDPRef, productArea.product.product_id, isOverlay]);

    return (
        <>
            <View marginBottom="md">
                {productBreadcrumbs && (
                    <Breadcrumbs type="pdp" breadcrumbs={productBreadcrumbs.breadcrumbs} />
                )}
            </View>
            <View marginBottom="md">
                <Heading as="h4" textStyle="body-bold">
                    {gettext("oos_product.full_product_details")}
                </Heading>
            </View>
            <div>
                <View width="full" marginRight="md" className={styles.detailsAndGalleryContainer}>
                    <div className={styles.galleryParent} data-testid="product-area-image-gallery">
                        <View className={styles.imageGallery}>
                            <ProductImageGallery
                                images={productArea.product.images.map(
                                    ({ alt_text, full_size_url, id }, i) => ({
                                        id: id || `ProductImageGallery-${i.toString()}`,
                                        altText: alt_text,
                                        url: full_size_url,
                                    })
                                )}
                                productId={productArea.product.product_id}
                            />
                        </View>
                    </div>
                    <div className={styles.detailsSection}>
                        <div>
                            <Heading as="h1" textStyle="subhead" capitalize>
                                {productArea.product.designer}
                            </Heading>
                            <Text textStyle="body-s">{productArea.product.short_description}</Text>
                        </div>
                        <div data-testid="product-details-description">
                            <Accordion items={[productDetailsAccordion]} />
                        </div>
                        <div className={styles.backToTopContainer}>
                            <Button
                                variant="tertiary"
                                title={gettext("back_to_top")}
                                onClick={scrollToTop}
                                width="full"
                                dataTestId="back-to-top-button"
                            />
                        </div>
                    </div>
                </View>
            </div>
        </>
    );
}
